import { For, onCleanup, onMount, reactive, watch } from 'cdui-js';
import type { JSX } from 'cdui-js';
import './index.css';

/* ── 类型定义 ─────────────────────────────────────────────────────── */

export type DifiTabItem = {
  /** 唯一标识 */
  value: string | number;
  /** 标签文字或节点 */
  label: string | JSX.Element;
  /** 面板内容渲染函数（懒加载：首次切入时才会调用） */
  render?: () => JSX.Element;
  /** 是否禁用此 tab */
  disabled?: boolean;
};

/** DifiTabs 对外暴露的命令式方法 */
export type DifiTabsAPI = {
  /**
   * 使某个 tab 的内容失效，下次切入时重新调用 render()。
   * 如果该 tab 当前正在显示，立即重新渲染。
   * @param key 要刷新的 tab key，不传则刷新所有 tab
   */
  refreshTab: (key?: any) => void;
};

export type DifiTabsProps = {
  /** Tab 列表 */
  tabs: DifiTabItem[];
  /**
   * 当前激活 tab 的 key（受控模式）。
   * 不传时为非受控，内部自行维护激活状态。
   */
  active?: any;
  /** 切换 tab 时触发，参数为目标 tab 的 key */
  onChange?: (key: any) => void;
  /** 是否支持手势左右滑动切换，默认 true */
  swipeable?: boolean;
  class?: string;
  style?: JSX.CSSProperties;
  /**
   * 组件挂载后回调，将命令式 API 传给父组件。
   * @example
   * let tabsAPI = {} as DifiTabsAPI;
   * <DifiTabs expose={(api) => { tabsAPI = api; }} ... />
   * // 之后调用：tabsAPI.refreshTab('hk')
   */
  expose?: (api: DifiTabsAPI) => void;
};

/* ── 组件 ─────────────────────────────────────────────────────────── */

/**
 * DifiTabs — 支持懒加载 + 手势滑动的 Tab 组件。
 *
 * 懒加载规则：某个面板在第一次被激活之前，其 `render()` 不会被调用；
 * 一旦访问过，内容将一直保留在 DOM 中（仅通过 translateX 隐藏）。
 *
 * 用法示例：
 * ```tsx
 * const state = reactive({ active: 'a' });
 *
 * <DifiTabs
 *   active={state.active}
 *   onChange={(key) => { state.active = key; }}
 *   tabs={[
 *     { key: 'a', title: '标签 1', render: () => <div>内容 1</div> },
 *     { key: 'b', title: '标签 2', render: () => <div>内容 2</div> },
 *   ]}
 * />
 * ```
 */
export const DifiTabs = (props: DifiTabsProps) => {
  /* tabItems 始终指向 props.tabs 的最新值，供各逻辑函数使用 */
  const getTabs = () => props.tabs ?? [];
  const swipeable = props.swipeable ?? true;

  const initialKey = (): string | number | undefined => props.active ?? getTabs()[0]?.value;

  const state = reactive({
    activeKey: initialKey() as string | number | undefined,
    /** 已访问过的 tab key 列表（懒加载控制） */
    visited: initialKey() != null ? [initialKey() as string | number] : ([] as (string | number)[]),
    /** 当前激活 tab 在数组中的索引（用于 translateX 计算） */
    activeIndex: getTabs().findIndex((t) => t.value === initialKey()),
  });

  /* 受控模式：外部 active 变化时同步内部状态 */
  watch(
    () => props.active,
    (val) => {
      if (val !== undefined && val !== state.activeKey) {
        activateTab(val, false);
      }
    }
  );

  /* 后端数据返回后 tabs 更新：重新初始化激活状态 */
  watch(
    () => props.tabs,
    (val) => {
      if (!val || val.length === 0) return;

      const currentValid = val.some((t) => t.value === state.activeKey);
      if (currentValid) {
        /* 当前激活 key 仍然合法，只需刷新 index（tabs 可能重排） */
        state.activeIndex = val.findIndex((t) => t.value === state.activeKey);
        requestAnimationFrame(() => {
          updateActiveBar(state.activeKey!);
          scrollHeaderToActive(state.activeKey!);
        });
      } else {
        /* 初次加载或 tabs 完全替换：取 props.active 或第一个 tab */
        const firstKey = props.active != null && val.some((t) => t.value === props.active)
          ? props.active
          : val[0].value;
        state.activeKey = firstKey;
        state.activeIndex = val.findIndex((t) => t.value === firstKey);
        state.visited = [firstKey];
        requestAnimationFrame(() => {
          updateActiveBar(firstKey);
          scrollHeaderToActive(firstKey);
        });
      }
    }
  );


  /* DOM 引用 */
  const tabRefs = new Map<string | number, HTMLElement>();
  let activeBarEl: HTMLElement;
  let headerEl: HTMLElement;
  let trackEl: HTMLElement;
  let contentEl: HTMLElement;

  /* ── 核心：切换 tab ──────────────────────────────────────────────── */
  const activateTab = (key: string | number, emitChange = true) => {
    const tabs = getTabs();
    const idx = tabs.findIndex((t) => t.value === key);
    if (idx === -1) return;
    const tab = tabs[idx];
    if (tab.disabled) return;

    /* 懒加载：首次访问才加入 visited，触发内容渲染 */
    if (!state.visited.includes(key)) {
      state.visited = [...state.visited, key];
    }

    state.activeKey = key;
    state.activeIndex = idx;

    updateActiveBar(key);
    scrollHeaderToActive(key);

    if (emitChange) props.onChange?.(key);
  };

  /* ── 标签栏激活条位置更新 ─────────────────────────────────────────── */
  const updateActiveBar = (key: string | number) => {
    if (!activeBarEl || !headerEl) return;
    const tabEl = tabRefs.get(key);
    if (!tabEl) return;

    const headerRect = headerEl.getBoundingClientRect();
    const tabRect = tabEl.getBoundingClientRect();
    /* left 相对于 header 含已滚动偏移 */
    const relLeft = tabRect.left - headerRect.left + headerEl.scrollLeft;

    activeBarEl.style.left = `${relLeft + 8}px`;
    activeBarEl.style.width = `${tabRect.width - 16}px`;
  };

  /* ── 让激活标签居中滚动到视野内 ─────────────────────────────────── */
  const scrollHeaderToActive = (key: string | number) => {
    if (!headerEl) return;
    const tabEl = tabRefs.get(key);
    if (!tabEl) return;

    const containerCenter = headerEl.getBoundingClientRect().left + headerEl.clientWidth / 2;
    const tabCenter = tabEl.getBoundingClientRect().left + tabEl.clientWidth / 2;
    const targetScroll = headerEl.scrollLeft + (tabCenter - containerCenter);
    headerEl.scrollTo({ left: targetScroll, behavior: 'smooth' });
  };

  /* ── 命令式 API ─────────────────────────────────────────────────── */
  const refreshTab = (key?: string | number) => {
    if (key === undefined) {
      /*
       * 刷新所有 tab。
       *
       * ❌ 错误写法：state.visited = [activeKey]
       *    当前 tab 的条件 true→true，组件从不销毁，fetchList 不重新执行。
       *
       * ✅ 正确做法：分两帧
       *    帧1：state.visited = []        → 所有面板条件变 false → 子组件全部销毁
       *    帧2：state.visited = [activeKey] → 当前面板重建 → Stocks 重新实例化 → fetchList 重新执行
       *    其他 tab 保持懒加载状态，下次切入时再重建。
       */
      const activeKey = state.activeKey;
      state.visited = [];
      requestAnimationFrame(() => {
        state.visited = [activeKey];
      });
    } else {
      /*
       * 刷新指定 tab：先移除（销毁），若是当前 tab 则下一帧重建。
       * 非当前 tab 下次切入时自动重建（正常懒加载流程）。
       */
      const wasActive = key === state.activeKey;
      state.visited = state.visited.filter((k) => k !== key);
      if (wasActive) {
        requestAnimationFrame(() => {
          state.visited = [...state.visited, key];
        });
      }
    }
  };

  onMount(() => {
    updateActiveBar(state.activeKey);

    if (swipeable && contentEl) {
      /* passive: true  → touchstart/end 不需要 preventDefault，让浏览器自由优化 */
      /* passive: false → touchmove 需要调用 preventDefault() 阻止纵向滚动，必须非被动 */
      contentEl.addEventListener('touchstart', onTouchStart, { passive: true });
      contentEl.addEventListener('touchmove', onTouchMove, { passive: false });
      contentEl.addEventListener('touchend', onTouchEnd, { passive: true });
    }

    /* 将命令式 API 暴露给父组件 */
    props.expose?.({ refreshTab });
  });

  onCleanup(() => {
    if (swipeable && contentEl) {
      contentEl.removeEventListener('touchstart', onTouchStart);
      contentEl.removeEventListener('touchmove', onTouchMove);
      contentEl.removeEventListener('touchend', onTouchEnd);
    }
  });

  /* ── 手势滑动 ───────────────────────────────────────────────────── */
  let touchStartX = 0;
  let touchStartY = 0;
  let isHorizontalSwipe = false; // 本次触摸是否判断为水平滑动
  let swipeDetected = false; // 是否已完成方向判断

  /* ── 指示条插值（拖动时跟手） ──────────────────────────────────────── */
  const interpolateBar = (rawDeltaX: number) => {
    if (!activeBarEl || !headerEl || !contentEl) return;

    const idx = state.activeIndex;
    const containerWidth = contentEl.clientWidth || window.innerWidth;
    /* 进度 0~1，按容器宽度归一化 */
    const progress = Math.min(1, Math.abs(rawDeltaX) / containerWidth);

    const tabs = getTabs();
    /* 相邻 tab 的方向 */
    const adjacentIdx = rawDeltaX < 0 && idx < tabs.length - 1 ? idx + 1 : rawDeltaX > 0 && idx > 0 ? idx - 1 : -1;

    if (adjacentIdx === -1) return; // 边缘，不插值

    const curTabEl = tabRefs.get(tabs[idx].value);
    const adjTabEl = tabRefs.get(tabs[adjacentIdx].value);
    if (!curTabEl || !adjTabEl) return;

    const headerRect = headerEl.getBoundingClientRect();
    const curRect = curTabEl.getBoundingClientRect();
    const adjRect = adjTabEl.getBoundingClientRect();

    /* 计算各自的 left / width（与 updateActiveBar 逻辑一致） */
    const curLeft = curRect.left - headerRect.left + headerEl.scrollLeft + 8;
    const curW = curRect.width - 16;
    const adjLeft = adjRect.left - headerRect.left + headerEl.scrollLeft + 8;
    const adjW = adjRect.width - 16;

    activeBarEl.style.left = `${curLeft + (adjLeft - curLeft) * progress}px`;
    activeBarEl.style.width = `${curW + (adjW - curW) * progress}px`;
  };

  const onTouchStart = (e: TouchEvent) => {
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    isHorizontalSwipe = false;
    swipeDetected = false;

    /* 拖拽时关闭 transition，避免跟随手指时有延迟感 */
    if (trackEl) trackEl.style.transition = 'none';
    if (activeBarEl) activeBarEl.style.transition = 'none';
  };

  const onTouchMove = (e: TouchEvent) => {
    if (!swipeDetected) {
      const dx = Math.abs(e.touches[0].clientX - touchStartX);
      const dy = Math.abs(e.touches[0].clientY - touchStartY);
      /* 移动超过 5px 时判断方向 */
      if (dx > 5 || dy > 5) {
        isHorizontalSwipe = dx > dy;
        swipeDetected = true;
      }
    }

    if (!isHorizontalSwipe) return;

    /* 阻止页面上下滚动 */
    e.preventDefault();

    const rawDeltaX = e.touches[0].clientX - touchStartX;
    const idx = state.activeIndex;

    /* 边缘阻尼：第一/最后一个 tab 继续往外拉时给 0.25 倍阻力 */
    let deltaX = rawDeltaX;
    if ((rawDeltaX > 0 && idx === 0) || (rawDeltaX < 0 && idx === getTabs().length - 1)) {
      deltaX = rawDeltaX * 0.25;
    }

    /* 内容区域实时跟手 */
    if (trackEl) {
      trackEl.style.transform = `translateX(calc(-${idx * 100}% + ${deltaX}px))`;
    }

    /* 指示条实时跟手（用未衰减的 rawDeltaX 计算进度） */
    interpolateBar(rawDeltaX);
  };

  const onTouchEnd = (e: TouchEvent) => {
    if (!isHorizontalSwipe) {
      /* 非水平滑动：恢复 transition 即可，内容未被移动 */
      if (trackEl) trackEl.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      if (activeBarEl) activeBarEl.style.transition = 'left 0.25s ease, width 0.25s ease';
      return;
    }

    const deltaX = e.changedTouches[0].clientX - touchStartX;
    const minSwipe = 50;
    const idx = state.activeIndex;

    const tabs = getTabs();
    if (deltaX < -minSwipe && idx < tabs.length - 1) {
      /* 向左滑超过阈值 → 切换到下一个 tab */
      if (trackEl) trackEl.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      if (activeBarEl) activeBarEl.style.transition = 'left 0.25s ease, width 0.25s ease';
      activateTab(tabs[idx + 1].value);
    } else if (deltaX > minSwipe && idx > 0) {
      /* 向右滑超过阈值 → 切换到上一个 tab */
      if (trackEl) trackEl.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      if (activeBarEl) activeBarEl.style.transition = 'left 0.25s ease, width 0.25s ease';
      activateTab(tabs[idx - 1].value);
    } else {
      /*
       * 未达到切换阈值 → 回弹到当前 tab。
       *
       * 关键：必须先让浏览器渲染一帧（transition: none → transition: 0.3s），
       * 再在下一帧改 transform，否则两次样式修改合并在同一帧，
       * 浏览器找不到"起点"，动画无法触发，内容会直接瞬移。
       */
      requestAnimationFrame(() => {
        if (trackEl) trackEl.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        if (activeBarEl) activeBarEl.style.transition = 'left 0.25s ease, width 0.25s ease';

        requestAnimationFrame(() => {
          if (trackEl) trackEl.style.transform = `translateX(-${idx * 100}%)`;
          if (tabs[idx]) updateActiveBar(tabs[idx].value);
        });
      });
    }
  };

  // const fixedHeaderStyle = props.fixedHeader ? { position: 'fixed', top: props.fixedHeader, width: '100%' } : null;

  /* ── 渲染 ──────────────────────────────────────────────────────── */
  return (
    <div class={`difi-tabs ${props.class || ''}`.trim()} style={props.style}>
      {/* ── 标签栏 ── */}
      {/* style={fixedHeaderStyle} */}
      <div ref={(el) => (headerEl = el)} class="difi-tabs__header">
        <For each={props.tabs ?? []}>
          {(tab) => (
            <div
              ref={(el) => el && tabRefs.set(tab.value, el)}
              class={`difi-tabs__tab${state.activeKey === tab.value ? ' color-D font-bold' : ' color-L'}${tab.disabled ? ' difi-tabs__tab--disabled' : ''
                }`}
              onclick={() => activateTab(tab.value)}
            >
              {tab.label}
            </div>
          )}
        </For>
        {/* 激活指示条，宽度/位置由 JS 动态写入 */}
        <div ref={(el) => (activeBarEl = el)} class="difi-tabs__bar" />
      </div>

      {/* ── 面板区域（touch 事件通过 onMount 手动注册，passive: false 才能 preventDefault） ── */}
      <div ref={(el) => (contentEl = el)} class="difi-tabs__content">
        <div
          ref={(el) => (trackEl = el)}
          class="difi-tabs__track"
          style={{
            transform: `translateX(-${state.activeIndex * 100}%)`,
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <For each={props.tabs ?? []}>
            {(tab) => (
              <div class="difi-tabs__panel">
                {/*
                 * 懒加载核心：
                 *   - state.visited 是顶层 reactive 属性，整体替换时触发响应。
                 *   - 只有 tab.value 首次进入 visited 后，render() 才被调用。
                 *   - 此处用 props.tabs（响应式）而非 tabItems（初始快照），
                 *     确保 getGroups() 刷新后 render 函数是最新的。
                 */}
                {state.visited.includes(tab.value) && tab.render ? tab.render() : null}
              </div>
            )}
          </For>
        </div>
      </div>
    </div>
  );
};
